import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { CreditBalance } from './Credits/CreditBalance'
import { Hoverable } from './Common/Hover'
import { CountBadge } from './Common/Chip'
import { IconButton } from './Common/IconButton'
import { Pressable } from './Common/Pressable'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import VideoCameraFrontRoundedIcon from '@mui/icons-material/VideoCameraFrontRounded'
import DynamicFormRoundedIcon from '@mui/icons-material/DynamicFormRounded'
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded'
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded'
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded'
import SummarizeRoundedIcon from '@mui/icons-material/SummarizeRounded'
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded'
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded'
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import ContactMailRoundedIcon from '@mui/icons-material/ContactMailRounded'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded'
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded'
import SportsRoundedIcon from '@mui/icons-material/SportsRounded'
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded'
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded'
import LocationCityRoundedIcon from '@mui/icons-material/LocationCityRounded'
import LayersRoundedIcon from '@mui/icons-material/LayersRounded'
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded'
import HotelRoundedIcon from '@mui/icons-material/HotelRounded'
import HandymanRoundedIcon from '@mui/icons-material/HandymanRounded'
import MedicalServicesRoundedIcon from '@mui/icons-material/MedicalServicesRounded'
import FactoryRoundedIcon from '@mui/icons-material/FactoryRounded'
import FitnessCenterRoundedIcon from '@mui/icons-material/FitnessCenterRounded'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded'
import CenterFocusStrongRoundedIcon from '@mui/icons-material/CenterFocusStrongRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import ExploreRoundedIcon from '@mui/icons-material/ExploreRounded'
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded'
import DiamondRoundedIcon from '@mui/icons-material/DiamondRounded'
import FamilyRestroomRoundedIcon from '@mui/icons-material/FamilyRestroomRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded'
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import { useNotifications } from '../context/useNotifications'
import { useClientPreferences } from '../context/useClientPreferences'
import { usePersistedState } from '../hooks/usePersistedState'
import { useWorkspace } from '../context/useWorkspace'
import { NotificationHub } from './Notifications/NotificationHub'
import type { NotificationRouterHandle } from '../services/notificationRouter'
import { type BoardTileKey, getBoardRailRows } from './rightSideNavBoardLayout'
import { limitsLiftedForWorkspace } from '../utils/demoUnlock'
import { useNls } from '../nls/nlsProvider'
import { type ModuleKey, moduleForLayoutView } from '../utils/workspaceModules'
import { isPageHiddenForMe } from '../services/pageVisibilityService'
import type { Workspace } from '../types/workspace'

export type TileState = {
  key?: string
  label: string
  shortLabel?: string
  icon: ReactNode
  onClick?: () => void
  disabled?: boolean
  active?: boolean
  indicator?: boolean
  tourId?: string
}

export type RailTileState = TileState & {
  key: string
  section: 'board' | 'controls'
  badge?: number
}

export type RightSideNavProps = {
  userLabel: string
  userInitial: string
  userEmail?: string | null
  userPhotoUrl?: string | null
  workspacePlan?: unknown
  summary: { totalDays: number; rangeLabel: string } | null
  personalWalletCredits: number
  switchedOffModules?: ReadonlySet<ModuleKey>
  isPageHidden?: (key: BoardTileKey) => boolean
  // Board
  onOpenOverview?: () => void
  isOverviewOpen?: boolean
  isOverviewAvailable?: boolean
  onOpenDashboard: () => void
  isDashboardOpen: boolean
  onOpenCalendar?: () => void
  isCalendarAvailable: boolean
  isCalendarOpen: boolean
  onOpenMeetings?: () => void
  isMeetingsOpen: boolean
  onOpenForms?: () => void
  isFormsOpen: boolean
  onOpenAutomate?: () => void
  isAutomateOpen: boolean
  onOpenDesign?: () => void
  isDesignOpen: boolean
  onOpenChat?: () => void
  isChatAvailable: boolean
  isChatOpen: boolean
  onOpenRaid?: () => void
  isRaidActive: boolean
  isRaidPanelOpen: boolean
  isRaidAvailable: boolean
  onOpenForYou?: () => void
  isForYouOpen: boolean
  isForYouAvailable: boolean
  onOpenFocus?: () => void
  isFocusOpen?: boolean
  onOpenSummary?: () => void
  isSummaryOpen?: boolean
  onOpenAnalysis?: () => void
  isAnalysisOpen?: boolean
  onOpenReports?: () => void
  isReportsOpen?: boolean
  onOpenInvites: () => void
  isInvitesOpen?: boolean
  onOpenLink?: () => void
  isLinkOpen?: boolean
  onOpenStock?: () => void
  isStockOpen?: boolean
  onOpenClients?: () => void
  isClientsOpen?: boolean
  onOpenTimesheets?: () => void
  isTimesheetsOpen?: boolean
  onOpenBudget?: () => void
  isBudgetOpen?: boolean
  onOpenQuota?: () => void
  isQuotaOpen?: boolean
  onOpenTeaching?: () => void
  isTeachingOpen?: boolean
  onOpenTraining?: () => void
  isTrainingOpen?: boolean
  onOpenConstruction?: () => void
  isConstructionOpen?: boolean
  onOpenHospital?: () => void
  isHospitalOpen?: boolean
  onOpenSites?: () => void
  isSitesOpen?: boolean
  onOpenDelivery?: () => void
  isDeliveryOpen?: boolean
  onOpenShops?: () => void
  isShopsOpen?: boolean
  onOpenStays?: () => void
  isStaysOpen?: boolean
  onOpenJobs?: () => void
  isJobsOpen?: boolean
  onOpenClinics?: () => void
  isClinicsOpen?: boolean
  onOpenPlant?: () => void
  isPlantOpen?: boolean
  onOpenFitness?: () => void
  isFitnessOpen?: boolean
  onOpenUpload?: () => void
  isUploadOpen?: boolean
  onOpenWorkspaceSettings?: () => void
  isWorkspaceSettingsOpen?: boolean
  // Create
  onOpenAiMagic?: () => void
  isAiMagicOpen: boolean
  isAiMagicAvailable: boolean
  onOpenChecklists: () => void
  isChecklistsOpen?: boolean
  onOpenManage?: () => void
  isManageOpen: boolean
  isManageAvailable: boolean
  // More
  onOpenExplore?: () => void
  isExploreOpen?: boolean
  onOpenYourPage: () => void
  onOpenYourDetails?: () => void
  isYourPageOpen?: boolean
  // Controls
  onOpenPurchase?: () => void
  isPurchaseOpen?: boolean
  onOpenFamilyView?: () => void
  onOpenUserSettings?: () => void
  isUserSettingsOpen?: boolean
  // Footer
  onLogout: () => void
  notificationRouter?: NotificationRouterHandle
}

type TileProps = TileState & { showsLabel?: boolean }

const Tile = ({ label, shortLabel, icon, onClick, disabled = false, active = false, indicator = false, tourId, showsLabel = false }: TileProps) => {
  const button = (
    <Pressable
      className={`railTile${active ? ' isActive' : ''}`}
      onClick={onClick}
      disabled={disabled || !onClick}
      aria-label={label}
      data-tour-id={tourId}
    >
      <span className="railTileIcon" aria-hidden="true">
        {icon}
        {indicator && <span className="railTileIndicator" aria-hidden="true" />}
      </span>
      <span className="railTileLabel">
        <span className="railTileLabel__full">{label}</span>
        <span className="railTileLabel__short">{shortLabel ?? label}</span>
      </span>
    </Pressable>
  )
  // When the rail is expanded the label sits next to the icon, so a hover
  // bubble that repeats the same word is pure noise. In collapsed / slim mode
  // the icon stands alone and hover is the only way to identify a destination.
  if (showsLabel) return button
  return (
    <Hoverable
      variant="label"
      placement="bottom"
      offset={12}
      group="rightSideNav"
      content={{ title: label }}
    >
      {button}
    </Hoverable>
  )
}

const ICON_SIZE = 28

export type BuildRailNavItemsContext = {
  activeWorkspace?: Workspace
  t: ReturnType<typeof useNls>['t']
  notificationsBadgeCount?: number
  openNotificationsView?: () => void
  isNotificationsOpen?: boolean
}

// eslint-disable-next-line react-refresh/only-export-components
export function buildRightRailNavItems(
  props: RightSideNavProps,
  context: BuildRailNavItemsContext,
): {
  boardTiles: RailTileState[]
  boardTileRows: RailTileState[][]
  controlsTiles: RailTileState[]
} {
  const { t, activeWorkspace, notificationsBadgeCount = 0, openNotificationsView, isNotificationsOpen = false } = context

  const boardTileByKey: Partial<Record<BoardTileKey, RailTileState>> = {
    ...(props.isAiMagicAvailable || props.onOpenAiMagic
      ? {
          AI_MAGIC: {
            key: 'AI_MAGIC',
            section: 'board',
            label: t('NLSShellRailTileAiMagic'),
            shortLabel: t('NLSShellRailShortAi'),
            tourId: 'rail-ai-magic',
            icon: <AutoAwesomeRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
            onClick: props.onOpenAiMagic,
            disabled: !props.isAiMagicAvailable,
            active: props.isAiMagicOpen,
          },
        }
      : {}),
    DASHBOARD: {
      key: 'DASHBOARD',
      section: 'board',
      label: t('NLSShellRailTileDashboard'),
      shortLabel: t('NLSShellRailShortDashboard'),
      tourId: 'dashboard-rail',
      icon: <HomeRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenDashboard,
      active: props.isDashboardOpen,
    },
    ...(props.isOverviewAvailable
      ? {
          OVERVIEW: {
            key: 'OVERVIEW',
            section: 'board',
            label: t('NLSShellRailTileOverview'),
            shortLabel: t('NLSShellRailShortOverview'),
            tourId: 'rail-overview',
            icon: <MenuBookRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
            onClick: props.onOpenOverview,
            disabled: !props.onOpenOverview,
            active: props.isOverviewOpen,
          },
        }
      : {}),
    CALENDAR: {
      key: 'CALENDAR',
      section: 'board',
      label: t('NLSShellRailTileCalendar'),
      shortLabel: t('NLSShellRailShortCalendar'),
      tourId: 'rail-calendar',
      icon: <EventRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenCalendar,
      disabled: !props.isCalendarAvailable,
      active: props.isCalendarOpen,
    },
    MEETINGS: {
      key: 'MEETINGS',
      section: 'board',
      label: t('NLSShellRailTileMeetings'),
      shortLabel: t('NLSShellRailShortMeetings'),
      icon: <VideoCameraFrontRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenMeetings,
      disabled: !props.onOpenMeetings,
      active: props.isMeetingsOpen,
    },
    CHAT: {
      key: 'CHAT',
      section: 'board',
      label: t('NLSShellRailTileChat'),
      shortLabel: t('NLSShellRailTileChat'),
      tourId: 'rail-chat',
      icon: <ChatBubbleOutlineRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenChat,
      disabled: !props.isChatAvailable,
      active: props.isChatOpen,
    },
    TODOS: {
      key: 'TODOS',
      section: 'board',
      label: t('NLSShellRailTileTodos'),
      shortLabel: t('NLSShellRailShortTodos'),
      icon: <PersonSearchRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenForYou,
      disabled: !props.isForYouAvailable,
      active: props.isForYouOpen,
    },
    CHECKLISTS: {
      key: 'CHECKLISTS',
      section: 'board',
      label: t('NLSShellRailTileChecklists'),
      shortLabel: t('NLSShellRailShortChecklists'),
      tourId: 'rail-checklists',
      icon: <ChecklistRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenChecklists,
      active: props.isChecklistsOpen,
    },
    FORMS: {
      key: 'FORMS',
      section: 'board',
      label: t('NLSShellRailTileForms'),
      shortLabel: t('NLSShellRailTileForms'),
      tourId: 'rail-forms',
      icon: <DynamicFormRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenForms,
      disabled: !props.onOpenForms,
      active: props.isFormsOpen,
    },
    DESIGN: {
      key: 'DESIGN',
      section: 'board',
      label: t('NLSShellRailTileDesignHub'),
      shortLabel: 'Design',
      icon: <DashboardRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenDesign,
      disabled: !props.onOpenDesign,
      active: props.isDesignOpen,
    },
    UPLOAD: {
      key: 'UPLOAD',
      section: 'board',
      label: t('NLSShellRailTileUpload'),
      shortLabel: t('NLSShellRailTileUpload'),
      icon: <CloudUploadRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenUpload,
      disabled: !props.onOpenUpload,
      active: props.isUploadOpen,
    },
    INVITES: {
      label: t('NLSShellRailTileTeam'),
      shortLabel: t('NLSShellRailTileTeam'),
      key: 'INVITES',
      section: 'board',
      icon: <MailOutlineRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenInvites,
      active: props.isInvitesOpen,
    },
    LEDGER: {
      key: 'LEDGER',
      section: 'board',
      label: t('NLSShellRailTileLedger'),
      shortLabel: t('NLSShellRailTileLedger'),
      icon: <SummarizeRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenSummary,
      disabled: !props.onOpenSummary,
      active: props.isSummaryOpen,
    },
    ANALYSIS: {
      key: 'ANALYSIS',
      section: 'board',
      label: t('NLSShellRailTileAnalysis'),
      shortLabel: 'Analysis',
      icon: <InsightsRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenAnalysis,
      disabled: !props.onOpenAnalysis,
      active: props.isAnalysisOpen,
    },
    REPORTS: {
      key: 'REPORTS',
      section: 'board',
      label: t('NLSShellRailTileReports'),
      shortLabel: 'Report',
      icon: <AssessmentRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenReports,
      disabled: !props.onOpenReports,
      active: props.isReportsOpen,
    },
    LINK: {
      key: 'LINK',
      section: 'board',
      label: t('NLSShellRailTileLink'),
      shortLabel: t('NLSShellRailTileLink'),
      icon: <HubOutlinedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenLink,
      disabled: !props.onOpenLink,
      active: props.isLinkOpen,
    },
    STOCK: {
      key: 'STOCK',
      section: 'board',
      label: t('NLSShellRailTileStock'),
      shortLabel: t('NLSShellRailTileStock'),
      icon: <Inventory2RoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenStock,
      disabled: !props.onOpenStock,
      active: props.isStockOpen,
    },
    CLIENTS: props.onOpenClients ? {
      key: 'CLIENTS',
      section: 'board',
      label: t('NLSShellRailTileClients'),
      shortLabel: t('NLSShellRailTileClients'),
      icon: <ContactMailRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenClients,
      disabled: false,
      active: props.isClientsOpen,
    } : undefined as unknown as RailTileState,
    TIMESHEETS: props.onOpenTimesheets ? {
      key: 'TIMESHEETS',
      section: 'board',
      label: t('NLSShellRailTileTimesheets'),
      shortLabel: t('NLSShellRailTileTimesheets'),
      icon: <AccessTimeRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenTimesheets,
      disabled: false,
      active: props.isTimesheetsOpen,
    } : undefined as unknown as RailTileState,
    BUDGET: {
      key: 'BUDGET',
      section: 'board',
      label: t('NLSShellRailTileBudget'),
      shortLabel: t('NLSShellRailTileBudget'),
      icon: <PaidRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenBudget,
      disabled: !props.onOpenBudget,
      active: props.isBudgetOpen,
    },
    QUOTA: {
      key: 'QUOTA',
      section: 'board',
      label: t('NLSShellRailTileQuota'),
      shortLabel: t('NLSShellRailTileQuota'),
      icon: <TrackChangesRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenQuota,
      disabled: !props.onOpenQuota,
      active: props.isQuotaOpen,
    },
    TEACHING: {
      key: 'TEACHING',
      section: 'board',
      label: t('NLSShellRailTileTeaching'),
      shortLabel: t('NLSShellRailTileTeaching'),
      icon: <SchoolRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenTeaching,
      disabled: !props.onOpenTeaching,
      active: props.isTeachingOpen,
    },
    TRAINING: {
      key: 'TRAINING',
      section: 'board',
      label: t('NLSShellRailTileTraining'),
      shortLabel: t('NLSShellRailTileTraining'),
      icon: <SportsRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenTraining,
      disabled: !props.onOpenTraining,
      active: props.isTrainingOpen,
    },
    CONSTRUCTION: {
      key: 'CONSTRUCTION',
      section: 'board',
      label: t('NLSShellRailTileConstruction'),
      shortLabel: t('NLSShellRailTileConstruction'),
      icon: <ConstructionRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenConstruction,
      disabled: !props.onOpenConstruction,
      active: props.isConstructionOpen,
    },
    HOSPITAL: {
      key: 'HOSPITAL',
      section: 'board',
      label: t('NLSShellRailTileHospital'),
      shortLabel: t('NLSShellRailTileHospital'),
      icon: <LocalHospitalRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenHospital,
      disabled: !props.onOpenHospital,
      active: props.isHospitalOpen,
    },
    SITES: {
      key: 'SITES',
      section: 'board',
      label: t('NLSShellRailTileSites'),
      shortLabel: t('NLSShellRailTileSites'),
      icon: <LocationCityRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenSites,
      disabled: !props.onOpenSites,
      active: props.isSitesOpen,
    },
    DELIVERY: {
      key: 'DELIVERY',
      section: 'board',
      label: t('NLSShellRailTileDelivery'),
      shortLabel: t('NLSShellRailTileDelivery'),
      icon: <LayersRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenDelivery,
      disabled: !props.onOpenDelivery,
      active: props.isDeliveryOpen,
    },
    SHOPS: {
      key: 'SHOPS',
      section: 'board',
      label: t('NLSShellRailTileShops'),
      shortLabel: t('NLSShellRailTileShops'),
      icon: <StorefrontRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenShops,
      disabled: !props.onOpenShops,
      active: props.isShopsOpen,
    },
    STAYS: {
      key: 'STAYS',
      section: 'board',
      label: t('NLSShellRailTileStays'),
      shortLabel: t('NLSShellRailTileStays'),
      icon: <HotelRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenStays,
      disabled: !props.onOpenStays,
      active: props.isStaysOpen,
    },
    JOBS: {
      key: 'JOBS',
      section: 'board',
      label: t('NLSShellRailTileJobs'),
      shortLabel: t('NLSShellRailTileJobs'),
      icon: <HandymanRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenJobs,
      disabled: !props.onOpenJobs,
      active: props.isJobsOpen,
    },
    CLINICS: {
      key: 'CLINICS',
      section: 'board',
      label: t('NLSShellRailTileClinics'),
      shortLabel: t('NLSShellRailTileClinics'),
      icon: <MedicalServicesRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenClinics,
      disabled: !props.onOpenClinics,
      active: props.isClinicsOpen,
    },
    PLANT: {
      key: 'PLANT',
      section: 'board',
      label: t('NLSShellRailTilePlant'),
      shortLabel: t('NLSShellRailTilePlant'),
      icon: <FactoryRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenPlant,
      disabled: !props.onOpenPlant,
      active: props.isPlantOpen,
    },
    FITNESS: {
      key: 'FITNESS',
      section: 'board',
      label: t('NLSShellRailTileFitness'),
      shortLabel: t('NLSShellRailShortFitness'),
      icon: <FitnessCenterRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenFitness,
      disabled: !props.onOpenFitness,
      active: props.isFitnessOpen,
    },
    AUTOMATE: {
      key: 'AUTOMATE',
      section: 'board',
      label: t('NLSShellRailTileAutomate'),
      shortLabel: t('NLSShellRailShortAutomate'),
      icon: <BoltOutlinedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenAutomate,
      disabled: !props.onOpenAutomate,
      active: props.isAutomateOpen,
    },
    MANAGE: {
      key: 'MANAGE',
      section: 'board',
      label: t('NLSShellRailTileManage'),
      shortLabel: t('NLSShellRailTileManage'),
      icon: <TuneRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenManage,
      disabled: !props.isManageAvailable,
      active: props.isManageOpen,
    },
    ...(props.onOpenWorkspaceSettings
      ? {
          SETTINGS: {
            key: 'SETTINGS',
            section: 'board',
            label: t('NLSShellRailTileSettings'),
            shortLabel: 'Setup',
            tourId: 'rail-workspace-settings',
            icon: <AdminPanelSettingsRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
            onClick: props.onOpenWorkspaceSettings,
            active: props.isWorkspaceSettingsOpen,
          },
        }
      : {}),
    RAID: {
      key: 'RAID',
      section: 'board',
      label: t('NLSShellRailTileRaid'),
      shortLabel: t('NLSShellRailTileRaid'),
      icon: <LocalFireDepartmentRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenRaid,
      disabled: !props.isRaidAvailable || !props.onOpenRaid,
      active: props.isRaidPanelOpen,
      indicator: props.isRaidActive,
    },
    FOCUS: {
      key: 'FOCUS',
      section: 'board',
      label: t('NLSShellRailTileFocus'),
      shortLabel: t('NLSShellRailTileFocus'),
      tourId: 'rail-focus',
      icon: <CenterFocusStrongRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenFocus,
      disabled: !props.onOpenFocus,
      active: props.isFocusOpen,
    },
  }

  const boardTileRows = getBoardRailRows(props.workspacePlan, limitsLiftedForWorkspace(activeWorkspace))
    .map((row) => row
      .filter((key) => {
        const mod = moduleForLayoutView(key)
        if (mod && props.switchedOffModules?.has(mod)) return false
        if ((props.isPageHidden ?? isPageHiddenForMe)(key)) return false
        return true
      })
      .map((key) => boardTileByKey[key])
      .filter((tile): tile is RailTileState => Boolean(tile)))
    .filter((row) => row.length > 0)
  const boardTiles = boardTileRows.flat()

  const controlsTiles: RailTileState[] = [
    {
      key: 'YOUR_PAGE',
      section: 'controls',
      label: t('NLSShellRailTileYourPage'),
      tourId: 'rail-your-page',
      icon: <BadgeRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenYourPage,
      active: props.isYourPageOpen,
    },
    {
      key: 'EXPLORE',
      section: 'controls' as const,
      label: t('NLSShellRailTileExplore'),
      icon: <ExploreRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenExplore,
      disabled: !props.onOpenExplore,
      active: props.isExploreOpen,
    },
    {
      key: 'PURCHASE',
      section: 'controls' as const,
      label: t('NLSShellRailTilePurchase'),
      tourId: 'rail-purchase',
      icon: <DiamondRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenPurchase,
      disabled: !props.onOpenPurchase,
      active: props.isPurchaseOpen,
    },
    {
      key: 'NOTIFICATIONS',
      section: 'controls' as const,
      label: t('NLSShellRailTileNotifications'),
      tourId: 'rail-notifications',
      icon: (
        <>
          <NotificationsRoundedIcon sx={{ fontSize: ICON_SIZE }} />
          {notificationsBadgeCount > 0 && (
            <span aria-hidden="true">
              <CountBadge
                value={notificationsBadgeCount}
                max={99}
                className="railNotificationsBadge"
              />
            </span>
          )}
        </>
      ),
      onClick: openNotificationsView,
      active: isNotificationsOpen,
      badge: notificationsBadgeCount,
    },
    ...(props.onOpenFamilyView
      ? [
          {
            key: 'FAMILY_VIEW',
            section: 'controls' as const,
            label: t('NLSShellRailTileFamilyView'),
            icon: <FamilyRestroomRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
            onClick: props.onOpenFamilyView,
          },
        ]
      : []),
    {
      key: 'USER_SETTINGS',
      section: 'controls' as const,
      label: t('NLSShellRailTileSettings'),
      tourId: 'rail-user-settings',
      icon: <SettingsRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onOpenUserSettings,
      disabled: !props.onOpenUserSettings,
      active: props.isUserSettingsOpen,
    },
    {
      key: 'LOGOUT',
      section: 'controls' as const,
      label: t('NLSShellRailTileLogOut'),
      icon: <LogoutRoundedIcon sx={{ fontSize: ICON_SIZE }} />,
      onClick: props.onLogout,
    },
  ]

  return { boardTiles, boardTileRows, controlsTiles }
}

export const RightSideNav = (props: RightSideNavProps) => {
  const {
    userLabel,
    userInitial,
    userEmail,
    userPhotoUrl,
    summary,
    personalWalletCredits,
    notificationRouter,
  } = props
  const { t } = useNls()
  const [isPeekOpen, setIsPeekOpen] = usePersistedState<boolean>('rightNav.isPeekOpen', false)
  const { isSidebarPinned: isPinned, setSidebarPinned: setIsPinned, activeWorkspaceId, workspaces } = useWorkspace()
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId)
  const isExpanded = isPinned || isPeekOpen
  const { preferences } = useClientPreferences()
  const showCollapsedLabels = preferences.railCollapsedShowLabels
  const autoPopupOnHover = preferences.railAutoPopupOnHover

  const renderTile = (tile: TileState) => {
    const { key, ...tileProps } = tile
    return (
      <Tile
        key={key ?? tile.label}
        {...tileProps}
        showsLabel={isExpanded}
      />
    )
  }

  const { notifications } = useNotifications()
  const notificationsBadgeCount = notifications.filter((notification) => !notification.readAt).length
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)

  // Clicking the bell flips the rail. In slim mode we also peek-open so the
  // notifications face is actually readable; the unpinned auto-collapse hook
  // below flips us back as soon as the user mouses out.
  const openNotificationsView = () => {
    setIsPeekOpen(true)
    setIsNotificationsOpen(true)
  }

  // Unpinned + collapsed = the rail has shrunk to slim. That's our cue to
  // flip back to the nav side per the spec ("stays flipped only in expanded
  // mode"). When pinned the rail never collapses, so the flip persists until
  // the user hits the back chevron.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!isExpanded && isNotificationsOpen) setIsNotificationsOpen(false)
  }, [isExpanded, isNotificationsOpen])

  useEffect(() => {
    document.documentElement.dataset.railPinned = isPinned ? 'true' : 'false'
    return () => {
      delete document.documentElement.dataset.railPinned
    }
  }, [isPinned])


  // Scroll-fade tracker for the primary stack. Toggles data-fade-top /
  // data-fade-bottom on the scroll scope wrapper so the CSS ::before/::after
  // gradients can fade only when there's actually content out of view.
  const primaryStackRef = useRef<HTMLDivElement | null>(null)
  const railRef = useRef<HTMLElement | null>(null)
  const [stackFade, setStackFade] = useState<{ top: boolean; bottom: boolean }>({ top: false, bottom: false })

  // The flipper above wraps the scroll container in a 3D context
  // (`transform-style: preserve-3d` + `backface-visibility: hidden`), which
  // in WebKit/Blink can swallow wheel events before they reach the inner
  // `overflow: auto` scroller — letting the wheel bubble up and scroll the
  // page instead. Capture wheel on the rail itself and forward it to the
  // primary stack so scroll is always isolated to the nav.
  useEffect(() => {
    const rail = railRef.current
    if (!rail) return undefined
    const handler = (event: WheelEvent) => {
      const stack = primaryStackRef.current
      if (!stack) return
      if (stack.scrollHeight <= stack.clientHeight) return
      stack.scrollTop += event.deltaY
      event.preventDefault()
      event.stopPropagation()
    }
    rail.addEventListener('wheel', handler, { passive: false })
    return () => rail.removeEventListener('wheel', handler)
  }, [])

  useLayoutEffect(() => {
    const node = primaryStackRef.current
    if (!node) return undefined
    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = node
      const next = {
        top: scrollTop > 2,
        bottom: scrollTop + clientHeight < scrollHeight - 2,
      }
      setStackFade((prev) => (prev.top === next.top && prev.bottom === next.bottom ? prev : next))
    }
    update()
    node.addEventListener('scroll', update, { passive: true })
    const resize = new ResizeObserver(update)
    resize.observe(node)
    for (const child of Array.from(node.children)) resize.observe(child)
    return () => {
      node.removeEventListener('scroll', update)
      resize.disconnect()
    }
  }, [isExpanded, isPeekOpen])

  const { boardTiles, boardTileRows, controlsTiles } = buildRightRailNavItems(props, {
    activeWorkspace,
    t,
    notificationsBadgeCount,
    openNotificationsView,
    isNotificationsOpen,
  })

  return (
    <aside
      ref={railRef}
      className="rightRail"
      data-mode={isExpanded ? 'expanded' : 'slim'}
      data-pinned={isPinned ? 'true' : 'false'}
      data-view="menu"
      data-show-collapsed-labels={showCollapsedLabels ? 'true' : 'false'}
      onMouseEnter={() => {
        if (!isPinned && autoPopupOnHover) setIsPeekOpen(true)
      }}
      onMouseLeave={() => setIsPeekOpen(false)}
    >
      <div className="railProfile">
        <Pressable
          className="railProfileIdentityButton"
          onClick={props.onOpenYourDetails ?? props.onOpenYourPage}
          aria-label={t('NLSShellRailOpenYourDetails')}
        >
          <span className="railProfileAvatar" aria-hidden="true">
            {userPhotoUrl ? <img src={userPhotoUrl} alt="" /> : <span className="railProfileFallback">{userInitial}</span>}
          </span>
          <span className="railProfileInfo">
            <strong>{userLabel}</strong>
            {userEmail && <span>{userEmail}</span>}
          </span>
        </Pressable>
        <Hoverable
          variant="label"
          placement="left"
          group="rightSideNav"
          content={{ title: isPinned ? t('NLSShellRailUnpinSideNav') : t('NLSShellRailPinSideNav') }}
        >
          <IconButton
            variant="tertiary"
            size="sm"
            className="railPinButton"
            data-tour-id="dashboard-rail-toggle"
            onClick={() => setIsPinned((current) => !current)}
            pressed={isPinned}
            label={isPinned ? t('NLSShellRailUnpinSideNav') : t('NLSShellRailPinSideNav')}
            icon={isPinned ? <PushPinRoundedIcon sx={{ fontSize: 20 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 20 }} />}
          />
        </Hoverable>
      </div>

      <hr className="railDivider" />

      <div className="railFlipperOuter">
        <div
          className={`railFlipper${isNotificationsOpen && isExpanded ? ' railFlipper--flipped' : ''}`}
          data-flipped={isNotificationsOpen && isExpanded ? 'true' : 'false'}
        >
        <div className="railFlipperFace railFlipperFace--front">
          <div
            className="railScrollScope"
            data-fade-top={stackFade.top ? 'true' : 'false'}
            data-fade-bottom={stackFade.bottom ? 'true' : 'false'}
          >
            <div className="railPrimaryStack" ref={primaryStackRef}>
              <section
                className="railSection"
                style={{ '--rail-section-grow': boardTiles.length } as React.CSSProperties}
              >
                <h3 className="railSectionTitle">{t('NLSShellRailSectionBoard')}</h3>
                {summary && (
                  <div className="railBoardSummary">
                    <span className="railBoardSummaryLine">
                      <strong>{summary.totalDays.toLocaleString()}</strong> {t('NLSShellRailDays')}
                      <em>{summary.rangeLabel}</em>
                    </span>
                  </div>
                )}
                {isExpanded ? (
                  <div className="railTileGridRows">
                    {boardTileRows.map((row, rowIndex) => (
                      <div className="railTileGrid" key={`board-row-${rowIndex}`}>
                        {row.map((tile) => (
                          renderTile(tile)
                        ))}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="railTileGrid">
                    {boardTiles.map((tile) => (
                      renderTile(tile)
                    ))}
                  </div>
                )}
              </section>
            </div>
          </div>

          {isExpanded && (
            <section className="railSection railSection--controls">
              <hr className="railDivider" />
              <h3 className="railSectionTitle">{t('NLSShellRailSectionControls')}</h3>
              <div className="railPersonalWallet">
                <span>{t('NLSShellRailPersonalWallet')}</span>
                <strong>
                  <CreditBalance value={personalWalletCredits} variant="bare" scope="personal" label={null} />
                </strong>
              </div>
              <div className="railTileGrid railTileGrid--controls">
                {controlsTiles.map((tile) => (
                  renderTile(tile)

                ))}
              </div>
            </section>
          )}

          {!isExpanded && (
            <Hoverable
              variant="label"
              placement="left"
              group="rightSideNav"
              content={{ title: t('NLSShellRailExpandSideNav') }}
            >
              <IconButton
                variant="tertiary"
                size="sm"
                className="railChevronButton"
                onClick={() => setIsPeekOpen(true)}
                label={t('NLSShellRailExpandSideNav')}
                icon={<ChevronLeftRoundedIcon sx={{ fontSize: 22 }} />}
              />
            </Hoverable>
          )}
        </div>

        <div className="railFlipperFace railFlipperFace--back" aria-hidden={!isNotificationsOpen}>
          <NotificationHub
            onClose={() => setIsNotificationsOpen(false)}
            notificationRouter={notificationRouter}
          />
        </div>
        </div>
      </div>

    </aside>
  )
}
